import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Inventory2, Percent } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Material } from '../../../../sp-types/materialType';
import { SupplyPosition } from '../../../../services/supplyService';
import { useCategoryTerms } from '../../../../utils/categoryTerms';
import { useBaseCurrency } from '../../../../hooks/useBaseCurrency';
import { useFx } from '../../../../hooks/useFx';
import { ExposureHorizonWindow } from '../../../../utils/exposureHorizon';
import { filterByFormula, isOutOfScope as checkPositionOutOfScope } from '../../../../utils/positionUtils';
import { PositionsGrid, GroupByConfig } from '../../../../components/PositionsGrid';
import { getCurrencySymbol, formatCurrency, formatQuantity } from '../../../../components/PositionsGrid/gridUtils';

interface PriceInfo {
  price: number;
  currency: string;
  originalPrice?: number;
  originalCurrency?: string;
}

export interface SupplyGridProps {
  supplyPositions: SupplyPosition[];
  colorDictionary: Record<string, string>;
  materialsMap: Record<string, Material>;
  materialId: string;
  formulaName: string;
  pricesMap: Record<string, PriceInfo>;
  priceAsOfDate: string | null;
  unit: string;
  exposureWindow?: ExposureHorizonWindow;
}

export const SupplyGrid: React.FC<SupplyGridProps> = ({
  supplyPositions,
  colorDictionary,
  materialsMap,
  materialId,
  formulaName,
  pricesMap,
  priceAsOfDate,
  unit,
  exposureWindow,
}) => {
  const { supply: supplyTerm } = useCategoryTerms();
  const isGeneralMode = materialId === 'general';
  const baseCurrency = useBaseCurrency();
  const { convertToBase } = useFx(baseCurrency);
  const formulaColor = colorDictionary[materialId] || '#3b82f6';

  const formattedDate = priceAsOfDate
    ? new Date(priceAsOfDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : 'N/A';

  const filteredPositions = useMemo(() => {
    const base = materialId === 'general' || !materialId
      ? supplyPositions
      : filterByFormula(supplyPositions, materialId);
    // On-hand items first within every group level
    return [...base].sort((a, b) => {
      const aOnHand = a.supply_type === 'on_hand_inventory' ? 0 : 1;
      const bOnHand = b.supply_type === 'on_hand_inventory' ? 0 : 1;
      return aOnHand - bOnHand;
    });
  }, [supplyPositions, materialId]);

  // ── Row mapper ────────────────────────────────────────────
  const positionToRow = React.useCallback(
    (pos: SupplyPosition, idx: number) => {
      const isOnHand = pos.supply_type === 'on_hand_inventory';
      const isRatioPO = pos.supply_type === 'ratio_po';
      const priceInfo = pricesMap[pos.formula_code];
      const spotPrice = priceInfo?.price || 0;
      const quantity = Number(pos.quantity);
      const totalQty = Number(pos.total_quantity);
      const unitCost = Number(pos.unit_price_amount || 0);
      const unitCostCurrency = pos.unit_price_currency || 'USD';
      const { amountBase: purchaseValueRaw } = convertToBase(quantity * unitCost, unitCostCurrency);
      const purchaseValueBase = purchaseValueRaw ?? null;
      const spotValueBase = spotPrice > 0 ? quantity * spotPrice : 0;

      // Extract ratio value from notes for ratio PO positions (format: "Ratio PO (ratio=0.75, ...)")
      let ratioValue: number | null = null;
      if (isRatioPO && pos.notes) {
        const m = pos.notes.match(/ratio=([\d.]+)/);
        if (m) ratioValue = parseFloat(m[1]);
      }

      return {
        id: `${pos.id}-${idx}`,
        formula_code: pos.formula_code,
        formula_name: materialsMap[pos.formula_code]?.name || pos.formula_code,
        supply_type: isOnHand ? 'On Hand' : (isRatioPO ? 'Ratio' : 'PO'),
        po_reference: isOnHand ? '' : (pos.po_reference || '—'),
        supplier_name: pos.supplier_name || '—',
        customer_name: pos.customer_name || '—',
        delivery_period: pos.delivery_or_available_period,
        original_due_date: pos.original_due_date || null,
        quantity,
        total_quantity: totalQty,
        spot_price: spotPrice || null,
        spot_currency: priceInfo?.currency || 'USD',
        unit_cost: unitCost,
        unit_cost_currency: unitCostCurrency,
        purchase_value: purchaseValueBase,
        spot_value: spotValueBase,
        isOnHand,
        isRatioPO,
        ratioValue,
        isOutOfScope: exposureWindow ? checkPositionOutOfScope(pos, exposureWindow) : false,
      };
    },
    [pricesMap, materialsMap, convertToBase, exposureWindow],
  );

  // ── Summary row builder ─────────────────────────────────
  const buildSummaryRow = React.useCallback(
    (allRows: Record<string, any>[]) => {
      const calcRows = allRows.filter(r => !r.isRatioPO);
      const ratioRows = allRows.filter(r => r.isRatioPO);
      const totalQty = calcRows.reduce((s, r) => s + (r.quantity || 0), 0);
      const totalOriginal = calcRows.reduce((s, r) => s + (r.total_quantity || 0), 0);
      const totalPurchase = calcRows.reduce((s, r) => s + (r.purchase_value || 0), 0);
      const totalSpot = calcRows.reduce((s, r) => s + (r.spot_value || 0), 0);
      const ratioQty = ratioRows.reduce((s, r) => s + (r.quantity || 0), 0);

      // Weighted avg purchase price (always valid for any row set)
      const pricedRows = calcRows.filter(r => r.unit_cost > 0);
      const pricedQty = pricedRows.reduce((s, r) => s + (r.quantity || 0), 0);
      const pricedValue = pricedRows.reduce((s, r) => s + (r.purchase_value || 0), 0);

      // Spot price: only show when all rows share the same spot (same formula)
      const spotPrices = new Set(calcRows.map(r => r.spot_price).filter(Boolean));
      const sameSpot = spotPrices.size === 1;

      return {
        id: '__summary__',
        formula_code: '',
        formula_name: 'Total',
        supply_type: '',
        po_reference: '',
        supplier_name: '',
        customer_name: '',
        delivery_period: '',
        quantity: totalQty,
        total_quantity: totalOriginal,
        spot_price: sameSpot ? (calcRows[0]?.spot_price || null) : null,
        spot_currency: sameSpot ? (calcRows[0]?.spot_currency || '') : '',
        unit_cost: totalQty > 0 && pricedQty > 0 ? pricedValue / pricedQty : null,
        unit_cost_currency: totalQty > 0 && pricedQty > 0 ? baseCurrency : '',
        purchase_value: totalPurchase,
        spot_value: totalSpot,
        ratioQty,
        isOnHand: false,
        isSummary: true,
      };
    },
    [baseCurrency],
  );

  // ── Banner summary helper ─────────────────────────────────
  const buildSummary = React.useCallback(
    (children: Record<string, any>[]) => {
      const calcRows = children.filter(r => !r.isRatioPO);
      const ratioRows = children.filter(r => r.isRatioPO);
      const totalQty = calcRows.reduce((s, r) => s + (r.quantity || 0), 0);
      const totalSpot = calcRows.reduce((s, r) => s + (r.spot_value || 0), 0);
      const ratioQty = ratioRows.reduce((s, r) => s + (r.quantity || 0), 0);
      let text = `${formatQuantity(totalQty)} ${unit} · ${getCurrencySymbol(baseCurrency)}${formatCurrency(Math.round(totalSpot))} ${baseCurrency}`;
      if (ratioQty > 0) text += ` (+ ${formatQuantity(ratioQty)} ratio)`;
      return text;
    },
    [baseCurrency, unit],
  );

  // ── Group configurations ──────────────────────────────────
  const groupConfigs: GroupByConfig[] = useMemo(() => [
    {
      key: 'Formula',
      modes: ['general'],
      isDefaultFor: 'general',
      getGroupKey: (row) => row.formula_code,
      getGroupLabel: (key: string) => materialsMap[key]?.name || key,
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
      getGroupColor: (key: string) => colorDictionary[key] || '#94a3b8',
    },
    {
      key: 'Month',
      modes: ['general', 'formula'],
      isDefaultFor: 'formula',
      getGroupKey: (row) => row.delivery_period?.substring(0, 7) || 'Unknown',
      getGroupLabel: (key: string) => {
        const [y, m] = key.split('-');
        return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      },
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
    },
    {
      key: 'Customer',
      modes: ['formula'],
      getGroupKey: (row) => (row.customer_name && row.customer_name !== '—' ? row.customer_name : 'Unknown'),
      getGroupLabel: (key: string) => key,
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
    },
    {
      key: 'None',
      modes: ['formula'],
    },
  ], [materialsMap, colorDictionary, buildSummary]);

  // ── Column definitions ────────────────────────────────────
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'formula_name',
      headerName: 'Formula',
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => <div className="text-slate-700">{params.value}</div>,
    },
    {
      field: 'supply_type',
      headerName: 'Type',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params: any) => {
        const row = params.row;
        if (row.isOnHand) {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: `${formulaColor}15`, color: formulaColor }}>
              <Inventory2 sx={{ fontSize: 12 }} /> On Hand
            </span>
          );
        }
        if (row.isRatioPO) {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: `${formulaColor}15`, color: formulaColor }}>
              <Percent sx={{ fontSize: 12 }} /> Ratio
            </span>
          );
        }
        return <div className="text-slate-500 text-xs">{params.value}</div>;
      },
    },
    {
      field: 'supplier_name',
      headerName: 'Supplier',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''} arrow enterDelay={300}>
          <div className="text-slate-600 truncate">{params.value}</div>
        </Tooltip>
      ),
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''} arrow enterDelay={300}>
          <div className="text-slate-600 truncate">{params.value}</div>
        </Tooltip>
      ),
    },
    {
      field: 'delivery_period',
      headerName: 'Due Date',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        if (!params.value) return <div className="text-slate-400">—</div>;
        const date = new Date(params.value);
        const originalDue = params.row.original_due_date;
        const fmtDate = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        if (originalDue) {
          const origDate = new Date(originalDue);
          const origFmt = origDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          return (
            <Tooltip title={`Original due: ${origFmt} — moved to current month (overdue)`} arrow>
              <div className="flex items-center gap-1">
                <span className="text-amber-600 font-medium">{fmtDate}</span>
                <span className="text-[11px] text-amber-400 bg-amber-50 px-1 rounded">overdue</span>
              </div>
            </Tooltip>
          );
        }
        return <div className="text-slate-600">{fmtDate}</div>;
      },
    },
    {
      field: 'quantity',
      headerName: `Quantity (${unit})`,
      flex: 0.7,
      minWidth: 110,
      renderCell: (params: any) => {
        const remaining = Number(params.value);
        const total = Number(params.row.total_quantity);
        const showTotal = total > 0 && total !== remaining;
        const ratioQty = params.row.ratioQty;
        return (
          <div className="text-slate-700">
            {formatQuantity(remaining)}{showTotal && <span className="text-xs text-slate-400"> of {formatQuantity(total)}</span>}
            {ratioQty > 0 && <span className="text-xs text-slate-400 ml-1">(+ {formatQuantity(ratioQty)} ratio)</span>}
          </div>
        );
      },
    },
    {
      field: 'spot_price',
      headerName: 'Spot Price',
      flex: 0.7,
      minWidth: 110,
      renderHeader: () => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold">Spot Price</span>
          <span className="text-[11px] text-slate-400 font-normal">as of {formattedDate}</span>
        </div>
      ),
      renderCell: (params: any) => {
        const price = params.value;
        const isAgg = params.row.isSummary || params.row._isGroupRow;
        if (!price) return (isAgg && isGeneralMode) ? <div /> : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const cur = params.row.spot_currency || baseCurrency;
        return (
          <div className="text-slate-600">
            {getCurrencySymbol(cur)}{Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-xs text-slate-400">{cur}</span>
          </div>
        );
      },
    },
    {
      field: 'unit_cost',
      headerName: 'Purchase Price',
      flex: 0.9,
      minWidth: 140,
      renderCell: (params: any) => {
        const cost = Number(params.value);
        const isAgg = params.row.isSummary || params.row._isGroupRow;
        if (params.row.isRatioPO && params.row.ratioValue != null) {
          return (
            <Tooltip title="Fixed ratio against spot price" arrow enterDelay={300}>
              <div className="text-slate-700 flex items-center gap-1">
                <span>×{params.row.ratioValue}</span>
                <span className="text-[10px] font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded px-1 leading-tight">ratio</span>
              </div>
            </Tooltip>
          );
        }
        if (!cost || cost <= 0) return <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const cur = params.row.unit_cost_currency;
        const isDifferentCurrency = !isAgg && cur && cur !== 'MULTI' && cur.toUpperCase() !== baseCurrency.toUpperCase();
        const { amountBase } = isDifferentCurrency ? convertToBase(cost, cur) : { amountBase: cost };
        return (
          <Tooltip title={isAgg ? 'Weighted average purchase price' : ''} arrow enterDelay={300}>
            <div className="flex flex-col leading-tight">
              <span className="text-slate-700 flex items-center gap-1">
                {getCurrencySymbol(cur)}{cost.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-xs text-slate-400">{cur}</span>
                {isAgg && <span className="text-[10px] font-medium text-slate-300 bg-slate-50 border border-slate-200 rounded px-1 leading-tight">avg</span>}
              </span>
              {isDifferentCurrency && amountBase === null && (
                <span className="text-[11px] text-slate-300 italic">Converting…</span>
              )}
              {isDifferentCurrency && amountBase !== null && amountBase > 0 && (
                <span className="text-[11px] text-slate-400/70 italic">≈ {getCurrencySymbol(baseCurrency)}{amountBase.toLocaleString(undefined, { maximumFractionDigits: 0 })} {baseCurrency}</span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'purchase_value',
      headerName: `Purchase Value (${baseCurrency})`,
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        if (params.value === null) return <div className="text-slate-300 text-[11px] italic">Converting…</div>;
        const v = Number(params.value);
        return v > 0 ? (
          <div className="text-slate-700">{getCurrencySymbol(baseCurrency)}{formatCurrency(Math.round(v))} <span className="text-xs text-slate-400">{baseCurrency}</span></div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
    {
      field: 'spot_value',
      headerName: `Spot Value (${baseCurrency})`,
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold">Spot Value ({baseCurrency})</span>
          <span className="text-[11px] text-slate-400 font-normal">as of {formattedDate}</span>
        </div>
      ),
      renderCell: (params: any) => {
        const v = Number(params.value);
        return v > 0 ? (
          <div className="text-slate-700">{getCurrencySymbol(baseCurrency)}{formatCurrency(Math.round(v))} <span className="text-xs text-slate-400">{baseCurrency}</span></div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
    {
      field: 'po_reference',
      headerName: 'P.O.',
      flex: 0.7,
      minWidth: 130,
      renderCell: (params: any) => <div className="text-slate-500 text-xs">{params.value || ''}</div>,
    },
  ], [baseCurrency, unit, formulaColor, formattedDate, isGeneralMode]);

  const formulaCount = isGeneralMode ? new Set(supplyPositions.map(p => p.formula_code)).size : undefined;

  // ── Render ────────────────────────────────────────────────
  return (
    <PositionsGrid
      positions={filteredPositions}
      positionToRow={positionToRow}
      columns={columns}
      groupConfigs={groupConfigs}
      buildSummaryRow={buildSummaryRow}
      categoryTerm={supplyTerm}
      isGeneralMode={isGeneralMode}
      formulaId={materialId}
      formulaName={formulaName}
      formulaColor={formulaColor}
      uniqueFormulas={formulaCount}
    />
  );
};

export default SupplyGrid;
